import { useCallback, useSyncExternalStore } from "react";
import type { NewFeatureState } from "../types";

const STORAGE_KEY = "artis-menu-new-features";

let cachedState: NewFeatureState = {};
let cachedJson: string | null = null;

const getSnapshot = (): NewFeatureState => {
  try {
    const text = localStorage.getItem(STORAGE_KEY);
    if (text === cachedJson) return cachedState;
    cachedJson = text;
    if (!text) {
      cachedState = {};
      return cachedState;
    }
    const parsed = JSON.parse(text) as { value?: NewFeatureState };
    cachedState = parsed.value ?? {};
    return cachedState;
  } catch {
    cachedState = {};
    return cachedState;
  }
};

const subscribe = (callback: () => void) => {
  window.addEventListener("storage", callback);
  return () => window.removeEventListener("storage", callback);
};

export const useNewFeatureTracking = () => {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const isNew = useCallback(
    (id: string, expiryDate?: boolean | Date | string | number): boolean => {
      if (state[id]?.dismissed) return false;

      if (expiryDate !== undefined && typeof expiryDate !== "boolean") {
        const expiry = new Date(expiryDate).getTime();
        if (Date.now() > expiry) return false;
      }

      return true;
    },
    [state],
  );

  const markAsUsed = useCallback((id: string) => {
    const now = Date.now();
    const currentState = getSnapshot();
    const newState: NewFeatureState = {
      ...currentState,
      [id]: {
        dismissed: true,
        firstSeenAt: currentState[id]?.firstSeenAt || now,
        lastSeenAt: now,
        useCount: (currentState[id]?.useCount || 0) + 1,
      },
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ value: newState }));
    window.dispatchEvent(new StorageEvent("storage", { key: STORAGE_KEY }));
  }, []);

  return {
    isNew,
    markAsUsed,
  };
};
